import { Capacitor } from '@capacitor/core';
import { LocalNotifications, ScheduleOptions, LocalNotificationSchema } from '@capacitor/local-notifications';

export interface Reminder {
  id: string;
  user_id: string;
  title: string;
  message: string;
  reminder_time: string; // HH:MM:SS format
  reminder_date: string | null; // YYYY-MM-DD format, null for repeating
  repeat_type: 'once' | 'daily' | 'weekly' | 'monthly';
  day_of_week: number | null; // 0-6 for weekly
  day_of_month: number | null; // 1-31 for monthly
  is_active: boolean;
  sound_type: 'default' | 'loud' | 'alarm';
  last_triggered_at: string | null;
  created_at: string;
  updated_at: string;
}

// Generate a numeric ID from UUID (for local notifications)
const uuidToNumericId = (uuid: string): number => {
  // Use first 8 chars of UUID and convert to number
  const hex = uuid.replace(/-/g, '').substring(0, 8);
  return parseInt(hex, 16) % 2147483647; // Keep it within 32-bit int range
};

// Parse time string to hours and minutes
const parseTime = (timeStr: string): { hour: number; minute: number } => {
  const [hour, minute] = timeStr.split(':').map(Number);
  return { hour, minute };
};

// Get the next occurrence of a reminder
const getNextOccurrence = (reminder: Reminder): Date | null => {
  const now = new Date();
  const { hour, minute } = parseTime(reminder.reminder_time);

  switch (reminder.repeat_type) {
    case 'once': {
      if (!reminder.reminder_date) return null;
      const date = new Date(reminder.reminder_date);
      date.setHours(hour, minute, 0, 0);
      // If it's in the past, return null
      return date > now ? date : null;
    }

    case 'daily': {
      const date = new Date();
      date.setHours(hour, minute, 0, 0);
      // If time has passed today, schedule for tomorrow
      if (date <= now) {
        date.setDate(date.getDate() + 1);
      }
      return date;
    }

    case 'weekly': {
      if (reminder.day_of_week === null) return null;
      const date = new Date();
      date.setHours(hour, minute, 0, 0);
      
      // Find the next occurrence of this day of week
      const currentDay = date.getDay();
      let daysUntil = reminder.day_of_week - currentDay;
      
      if (daysUntil < 0 || (daysUntil === 0 && date <= now)) {
        daysUntil += 7;
      }
      
      date.setDate(date.getDate() + daysUntil);
      return date;
    }

    case 'monthly': {
      if (reminder.day_of_month === null) return null;
      const date = new Date();
      date.setDate(reminder.day_of_month);
      date.setHours(hour, minute, 0, 0);
      
      // If the date has passed this month, schedule for next month
      if (date <= now) {
        date.setMonth(date.getMonth() + 1);
      }
      
      return date;
    }

    default:
      return null;
  }
};

// Request notification permissions
export const requestNotificationPermissions = async (): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) {
    console.log('[Reminders] Not on native platform, skipping permission request');
    return false;
  }

  try {
    let permStatus = await LocalNotifications.checkPermissions();
    
    if (permStatus.display === 'prompt') {
      permStatus = await LocalNotifications.requestPermissions();
    }

    if (permStatus.display !== 'granted') {
      console.log('[Reminders] Notification permission not granted');
      return false;
    }

    console.log('[Reminders] Notification permission granted');
    return true;
  } catch (error) {
    console.error('[Reminders] Error requesting permissions:', error);
    return false;
  }
};

// Schedule a single reminder notification
export const scheduleReminderNotification = async (reminder: Reminder): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) {
    console.log('[Reminders] Not on native platform, skipping schedule');
    return false;
  }

  if (!reminder.is_active) {
    console.log('[Reminders] Reminder is not active, skipping');
    return false;
  }

  try {
    const nextOccurrence = getNextOccurrence(reminder);
    if (!nextOccurrence) {
      console.log('[Reminders] No next occurrence for reminder:', reminder.id);
      return false;
    }

    const notificationId = uuidToNumericId(reminder.id);

    // Cancel any existing notification for this reminder
    await LocalNotifications.cancel({ notifications: [{ id: notificationId }] });

    const notification: LocalNotificationSchema = {
      id: notificationId,
      title: reminder.title,
      body: reminder.message,
      schedule: {
        at: nextOccurrence,
        allowWhileIdle: true, // Important: allows notification when device is in Doze mode
      },
      sound: reminder.sound_type === 'loud' ? 'alarm.wav' : undefined,
      extra: {
        reminderId: reminder.id,
        repeatType: reminder.repeat_type,
      },
    };

    // For repeating reminders, set up repeating schedule
    if (reminder.repeat_type === 'daily') {
      notification.schedule = {
        ...notification.schedule,
        every: 'day',
        on: { hour: parseTime(reminder.reminder_time).hour, minute: parseTime(reminder.reminder_time).minute },
      };
    } else if (reminder.repeat_type === 'weekly' && reminder.day_of_week !== null) {
      notification.schedule = {
        ...notification.schedule,
        every: 'week',
        on: { 
          weekday: reminder.day_of_week + 1, // 1-7 in Capacitor (1 = Sunday)
          hour: parseTime(reminder.reminder_time).hour, 
          minute: parseTime(reminder.reminder_time).minute 
        },
      };
    } else if (reminder.repeat_type === 'monthly' && reminder.day_of_month !== null) {
      notification.schedule = {
        ...notification.schedule,
        every: 'month',
        on: { 
          day: reminder.day_of_month, 
          hour: parseTime(reminder.reminder_time).hour, 
          minute: parseTime(reminder.reminder_time).minute 
        },
      };
    }

    const scheduleOptions: ScheduleOptions = {
      notifications: [notification],
    };

    await LocalNotifications.schedule(scheduleOptions);
    console.log('[Reminders] Scheduled notification for:', nextOccurrence.toISOString());
    return true;
  } catch (error) {
    console.error('[Reminders] Error scheduling notification:', error);
    return false;
  }
};

// Cancel a reminder notification
export const cancelReminderNotification = async (reminderId: string): Promise<void> => {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const notificationId = uuidToNumericId(reminderId);
    await LocalNotifications.cancel({ notifications: [{ id: notificationId }] });
    console.log('[Reminders] Cancelled notification for reminder:', reminderId);
  } catch (error) {
    console.error('[Reminders] Error cancelling notification:', error);
  }
};

// Schedule all active reminders
export const scheduleAllReminders = async (reminders: Reminder[]): Promise<void> => {
  if (!Capacitor.isNativePlatform()) return;

  const hasPermission = await requestNotificationPermissions();
  if (!hasPermission) return;

  for (const reminder of reminders) {
    if (reminder.is_active) {
      await scheduleReminderNotification(reminder);
    }
  }
};

// Set up notification listeners
export const setupNotificationListeners = (): void => {
  if (!Capacitor.isNativePlatform()) return;

  // Listen for notification actions
  LocalNotifications.addListener('localNotificationActionPerformed', async (action) => {
    console.log('[Reminders] Notification action performed:', action);
    
    const reminderId = action.notification.extra?.reminderId;
    const repeatType = action.notification.extra?.repeatType;

    // If it's a one-time reminder, it's done
    // For repeating reminders, the system will handle rescheduling
    if (repeatType === 'once' && reminderId) {
      console.log('[Reminders] One-time reminder triggered:', reminderId);
      // Could update database to mark as triggered
    }
  });

  // Listen for notification received
  LocalNotifications.addListener('localNotificationReceived', (notification) => {
    console.log('[Reminders] Notification received:', notification);
  });
};

// Initialize reminders system
export const initializeReminders = async (): Promise<void> => {
  if (!Capacitor.isNativePlatform()) return;

  await requestNotificationPermissions();
  setupNotificationListeners();
};


